import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, User, Newspaper, Star } from "lucide-react";
import { AdBanner } from "./AdBanner";
import { ASNLoader } from "./ASNLoader";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [topStories, setTopStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
    fetchTopStories();
  }, []);

  const fetchArticles = async () => {
    try {
      const params = new URLSearchParams();
      params.append("limit", "50");
      
      const res = await axios.get(`${API}/news/articles?${params}`);
      setArticles(res.data.articles);
    } catch (err) {
      console.error("Error fetching articles:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopStories = async () => {
    try {
      const res = await axios.get(`${API}/news/top-stories`);
      setTopStories(res.data);
    } catch (err) {
      console.error("Error fetching top stories:", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  // Top story (priority 1) and featured stories (priority 2-3)
  const breakingStory = topStories.find(a => a.priority === 1);
  const featuredStories = topStories.filter(a => a.priority === 2 || a.priority === 3).slice(0, 2);
  
  // Regular articles excluding top stories
  const topStoryIds = topStories.map(a => a.id);
  const regularArticles = articles.filter(a => !topStoryIds.includes(a.id));

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="news-page">
      {/* Top Ad Banner */}
      <AdBanner placement="news" />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-zinc-900 to-[#050505] border-b border-white/5 pt-8 pb-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Newspaper className="w-8 h-8 text-white" />
            <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight text-white">
              News
            </h1>
          </div>
          <p className="text-zinc-400 text-lg max-w-2xl">
            Stay up to date with the latest stories, game recaps, and coverage from the Antler Sports Network.
          </p>
        </div>
      </section>

      {/* Top Stories Section */}
      {(breakingStory || featuredStories.length > 0) && (
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
          <div className="flex items-center gap-2 mb-6">
            <Star className="w-5 h-5 text-orange-500" />
            <h2 className="font-heading text-xl font-bold uppercase text-white">Top Stories</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Breaking Story - Takes 2 columns */}
            {breakingStory && (
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="md:col-span-2 group"
              >
                <Link to={`/news/${breakingStory.slug}`} className="block">
                  <div className="relative aspect-video bg-zinc-900 overflow-hidden">
                    {breakingStory.featured_image ? (
                      <img
                        src={breakingStory.featured_image}
                        alt={breakingStory.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-900 to-zinc-900">
                        <Newspaper className="w-16 h-16 text-zinc-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold uppercase mb-3">
                        Breaking
                      </span>
                      <h3 className="font-heading text-2xl md:text-3xl font-black uppercase tracking-tight text-white mb-2 group-hover:text-zinc-300 transition-colors">
                        {breakingStory.title}
                      </h3>
                      <p className="text-zinc-300 text-sm line-clamp-2 mb-3">
                        {breakingStory.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-zinc-400">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {breakingStory.author_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(breakingStory.published_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            )}

            {/* Featured Stories Stack */}
            {featuredStories.length > 0 && (
              <div className="space-y-4">
                {featuredStories.map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    className="group bg-zinc-900/50 border border-white/5 overflow-hidden hover:border-orange-500/50 transition-colors"
                  >
                    <Link to={`/news/${article.slug}`} className="flex gap-4 p-4">
                      {/* Thumbnail */}
                      <div className="w-24 h-20 flex-shrink-0 bg-zinc-800 overflow-hidden">
                        {article.featured_image ? (
                          <img
                            src={article.featured_image}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Newspaper className="w-6 h-6 text-zinc-700" />
                          </div>
                        )}
                      </div>
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <span className="inline-block px-2 py-0.5 bg-orange-500 text-black text-[10px] font-bold uppercase mb-1">
                          {article.priority === 2 ? "Top Story" : "Featured"}
                        </span>
                        <h4 className="font-heading text-sm font-bold uppercase text-white line-clamp-2 group-hover:text-zinc-300 transition-colors">
                          {article.title}
                        </h4>
                        <p className="text-xs text-zinc-500 mt-1">
                          {formatDate(article.published_at)}
                        </p>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* All Articles Grid */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 border-t border-white/5">
        <h2 className="font-heading text-xl font-bold uppercase text-white mb-6">Latest News</h2>
        
        {loading ? (
          <div className="flex justify-center py-16">
            <ASNLoader size="default" />
          </div>
        ) : regularArticles.length === 0 ? (
          <div className="text-center py-16">
            <Newspaper className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-white mb-2">No Articles Yet</h2>
            <p className="text-zinc-500">Check back soon for the latest news and stories.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularArticles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="group bg-zinc-900/50 border border-white/5 overflow-hidden hover:border-white/20 transition-colors"
              >
                <Link to={`/news/${article.slug}`}>
                  {/* Image */}
                  <div className="aspect-video bg-zinc-800 overflow-hidden">
                    {article.featured_image ? (
                      <img
                        src={article.featured_image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-10 h-10 text-zinc-700" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-2 line-clamp-2 group-hover:text-zinc-300 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-zinc-500 text-sm mb-3 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-zinc-600">
                      <span>{formatDate(article.published_at)}</span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {article.author_name}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </section>

      
    </main>
  );
};

export default NewsPage;
